import {
  FormControl,
  Select,
  MenuItem,
  Grid,
  FormHelperText,
  Typography,
  InputLabel,
  Chip,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { CustomFormContext } from "../../CustomForm";
import { ErrorInfo, Validation, Option } from "../../types";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { prepareContent } from "../../helpers";
import { useTranslation } from "react-i18next";
import { FieldTypeId } from "../../../../lib/enums";

interface CustomSelectProps<T extends Option> {
  nonForm?: boolean;
  fieldId: number;
  alias: string;
  header: string;
  typeId: FieldTypeId;
  readOnlyField?: boolean;
  fieldOptions?: T[] | string[] | undefined;
  defaultValue?: T | string | number | undefined;
  valueProp?: string;
  labelProp?: string;
  localized?: boolean;
  required?: boolean;
  className?: string;
  style?: React.CSSProperties;
  validations?: Validation[];
}

const getDefaultValue = <T extends Option>(
  options: T[] | string[],
  valueProp?: string,
  defaultValue?: T | string | number
) => {
  if (defaultValue !== undefined) {
    return defaultValue;
  }
  if (options.length > 0) {
    if (typeof options[0] === "string") {
      return options[0];
    } else if (valueProp) {
      return (options[0] as any)[valueProp];
    } else {
      return "";
    }
  }
  return "";
};

const injectDefaultValue = <T extends Option>(
  options: T[] | string[],
  valueProp?: string | number,
  labelProp?: string
) => {
  const newOptions = [...options];
  if (valueProp && labelProp) {
    newOptions.unshift({
      [valueProp]: "-1",
      [labelProp]: "Välj",
    } as T);
  } else {
    newOptions.unshift("Välj");
  }
  return newOptions;
};

const CustomSelect = <T extends Option>({
  nonForm = false,
  fieldId,
  alias,
  header,
  typeId,
  readOnlyField = false,
  localized = false,
  fieldOptions = [] as T[],
  defaultValue = undefined,
  valueProp = undefined,
  labelProp = undefined,
  required = false,
  className = "",
  style = {},
}: CustomSelectProps<T>) => {
  const {
    fieldValue,
    setDataDispatch,
    getValidationErrors,
    readOnlyState,
    dataIsSet,
    errorsAreSet,
    tempData,
    showErrors,
  } = useContext(CustomFormContext);
  const { t } = useTranslation();
  const [value, setValue] = useState<T | string | number | null>(null as any);
  const [errors, setErrors] = useState<ErrorInfo[] | null>(null);

  const options = injectDefaultValue(fieldOptions, valueProp, labelProp);
  useEffect(() => {
    if (!dataIsSet || !errorsAreSet) {
      return;
    }
    const data = fieldValue(alias);
    if (data === null || data === undefined) {
      return;
    }
    const errors = getValidationErrors(alias);
    if ((data !== undefined && value === null) || value !== data) {
      setValue(
        data || getDefaultValue(options as any, valueProp, defaultValue)
      );
    }
    if (errors) {
      setErrors(errors);
    }
  }, [dataIsSet, errorsAreSet, tempData]);

  const getLabel = (option: T | string) => {
    if (labelProp && option.hasOwnProperty(labelProp)) {
      return (option as any)[labelProp];
    } else if (valueProp && option.hasOwnProperty(valueProp)) {
      return (option as any)[valueProp];
    }
    return option;
  };

  const handleOnClick = (option: T | string | number) => {
    setValue(option);
  };

  const handleSave = () => {
    const data = prepareContent(value, alias, nonForm);
    setDataDispatch({ type: "UPDATE_CONTENT", payload: data });
  };

  useEffect(() => {
    handleSave();
  }, [value]);

  useEffect(() => {
    setErrors(getValidationErrors(alias));
  }, [getValidationErrors, alias]);

  return (
    <Grid item xs={12} sm={6} lg={4} className={className} style={style}>
      <InputLabel htmlFor={alias}>
        {header}{" "}
        {(required || errors?.find((a) => a.error === "isNotNullOrEmpty")) &&
          "*"}{" "}
        {localized && (
          <Chip size="small" label={t("customform.localized")} color="info" />
        )}
      </InputLabel>
      <FormControl style={{ width: "100%" }}>
        <Select
          value={value}
          readOnly={readOnlyField || readOnlyState}
          name={alias}
          disabled={readOnlyField || readOnlyState}
          onChange={(e) => {
            e.preventDefault();
            handleOnClick(e.target.value as T);
          }}
          IconComponent={(e) =>
            readOnlyState || readOnlyField ? null : (
              <ArrowDropDownIcon cursor="pointer" className={e.className} />
            )
          }
          data-test="eventlibrary-new-list-color-select"
        >
          {options.map((option, index) => {
            const value =
              valueProp && option.hasOwnProperty(valueProp)
                ? (option as any)[valueProp]
                : (option as string);
            return (
              <MenuItem
                key={`${index}-${value}`}
                value={value}
                sx={{
                  "&:hover": {
                    fontWeight: "700",
                    borderWidth: 3,
                    borderColor: "black",
                  },
                }}
                data-test="event-library-colors-menu-item"
              >
                {getLabel(option)}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {showErrors &&
        errors?.map((info, index) => (
          <FormHelperText key={index} variant="standard">
            <Typography variant="caption" color="red">
              {info.message}
            </Typography>
          </FormHelperText>
        ))}
    </Grid>
  );
};

CustomSelect.fieldType = "CustomSelect";

export default CustomSelect;
